/**
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: valeriaplutino
 * Created: 7/12/21
 */

import * as React from 'react';
import {
    GridItem,
    Heading,
    HStack,
    GridItemProps,
    AccordionPanel,
    AccordionItem,
    Accordion,
    AccordionButton,
    Box,
    SpaceProps,
} from '@chakra-ui/react';
import Icons from '@/components/icons/Icons';
import { lowerCase, startCase } from 'lodash';

interface TileProps {
    colspan: GridItemProps['colSpan'];
    children: React.ReactNode;
    heading?: string | React.ReactElement;
    collapsable?: boolean;
    action?: React.ReactElement;
    bg?: GridItemProps['bg'];
    overflowX?: GridItemProps['overflowX'];
    border?: GridItemProps['border'];
    borderColor?: GridItemProps['borderColor'];
    borderWidth?: GridItemProps['borderWidth'];
    rounded?: GridItemProps['rounded'];
    collapsableClick?: React.MouseEventHandler;
    role?: string;
    ariaLabelledBy?: string;
    mt?: SpaceProps['mt'];
    defaultOpen?: boolean;
}

interface TileHeadingProps {
    heading?: string | React.ReactElement;
    action?: React.ReactElement;
}

export const TileHeading = ({ heading, action }: TileHeadingProps) => {
    return (
        <HStack align={'center'} w={'full'} justify={'space-between'}>
            {typeof heading === 'string' ? (
                <Heading size={'small'} textColor={'blue.500'}>
                    {startCase(lowerCase(heading))}
                </Heading>
            ) : (
                heading
            )}

            {action}
        </HStack>
    );
};

export const Tile = (props: TileProps) => {
    const {
        bg = 'offWhite',
        overflowX,
        colspan,
        heading,
        action,
        collapsable,
        children,
        border,
        borderColor,
        borderWidth,
        rounded = 'none',
        collapsableClick,
        role,
        ariaLabelledBy,
        defaultOpen,
        mt,
    } = props;
    return (
        <GridItem
            bg={bg}
            rounded={rounded}
            overflowX={overflowX}
            colSpan={colspan}
            border={border}
            borderColor={borderColor}
            borderWidth={borderWidth}
            mt={mt}
        >
            {collapsable ? (
                <Accordion defaultIndex={defaultOpen ? [0] : []} allowToggle>
                    <AccordionItem border={'none'}>
                        {({ isExpanded }) => (
                            <>
                                <AccordionButton p={6} onClick={!isExpanded ? collapsableClick : undefined}>
                                    {isExpanded ? (
                                        <Icons.PointerUpIcon fontSize={'3xl'} />
                                    ) : (
                                        <Icons.PointerDownIcon fontSize={'3xl'} />
                                    )}
                                    <TileHeading heading={heading} action={action} />
                                </AccordionButton>
                                <AccordionPanel px={6}>{children}</AccordionPanel>
                            </>
                        )}
                    </AccordionItem>
                </Accordion>
            ) : (
                <Box role={role} aria-labelledby={ariaLabelledBy} p={6}>
                    <TileHeading heading={heading} action={action} />
                    {children}
                </Box>
            )}
        </GridItem>
    );
};
